import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { toast } from '#/shared/ui/sonner'

import type {
  DictationSession,
  SpeechRecognitionLike,
} from './dictation-support'
import { getSpeechRecognition } from './dictation-support'
import { playVoiceStart, playVoiceStop } from './voice-sounds'

/** Auto-stop after this much silence, mirroring the streaming engine, so an
 *  abandoned recording releases the mic instead of listening forever. */
const SILENCE_TIMEOUT_MS = 10_000

/** How often (ms) to poll the silence timer while listening. */
const SILENCE_POLL_MS = 500

/** i18n key for a recognition failure, or null for the errors that just mean
 *  "nothing was said / we cancelled" and shouldn't toast. */
function nativeErrorKey(error: string): string | null {
  switch (error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'voice.denied'
    case 'audio-capture':
      return 'voice.noMic'
    case 'no-speech':
    case 'aborted':
      return null
    default:
      return 'voice.error'
  }
}

/** Join the finalized segments in utterance order into one transcript. */
function joinFinals(finals: Map<number, string>): string {
  return [...finals.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, segment]) => segment)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** How a session reports back to the owning hook. */
interface SessionSink {
  setRecording: (value: boolean) => void
  setProcessing: (value: boolean) => void
  /** Deliver the final transcript (never called for an empty session). */
  emit: (text: string) => void
  /** Toast the failure behind the given i18n key. */
  fail: (key: string) => void
}

/** Mutable state of one recognition session, owned by its wired handlers. */
interface Session {
  recognition: SpeechRecognitionLike
  sink: SessionSink
  /** Finalized segments keyed by result index — iOS re-emits earlier results,
   *  and keying by index keeps exactly one copy of each. */
  finals: Map<number, string>
  /** Whether the mic actually opened (onstart fired), for the stop sound. */
  started: boolean
  /** Unmount: end the session without emitting into a gone surface. */
  suppressed: boolean
  lastVoiceAt: number
  timer: ReturnType<typeof setTimeout> | null
  /** Called once the session has settled, so the hook can drop it. */
  onSettled: () => void
}

function clearTimer(session: Session): void {
  if (session.timer) {
    clearTimeout(session.timer)
    session.timer = null
  }
}

/** While listening, poll for a prolonged pause and auto-finish — the native
 *  engines usually end on their own after silence, but Android Chrome in
 *  continuous mode happily listens forever. */
function watchSilence(session: Session): void {
  if (performance.now() - session.lastVoiceAt > SILENCE_TIMEOUT_MS) {
    stopSession(session)
    return
  }
  session.timer = setTimeout(() => watchSilence(session), SILENCE_POLL_MS)
}

/** User stop (and silence auto-stop): ask the engine to finish. Segments still
 *  finalizing settle in `onend`, usually within a beat. */
function stopSession(session: Session): void {
  clearTimer(session)
  session.sink.setRecording(false)
  if (session.started) session.sink.setProcessing(true)
  try {
    session.recognition.stop()
  } catch {
    // Already stopping — onend still fires and settles the session.
  }
}

/** Build a recognition session with its handlers wired, or null where the Web
 *  Speech API is missing. The caller starts it (and owns the start failure). */
function createSession(
  sink: SessionSink,
  onSettled: () => void,
): Session | null {
  const Recognition = getSpeechRecognition()
  if (!Recognition) return null
  const recognition = new Recognition()
  recognition.lang = 'en-US'
  recognition.continuous = true
  // Interim events double as the activity signal for the silence auto-stop;
  // only the finalized segments are kept.
  recognition.interimResults = true
  recognition.maxAlternatives = 1
  const session: Session = {
    recognition,
    sink,
    finals: new Map(),
    started: false,
    suppressed: false,
    lastVoiceAt: performance.now(),
    timer: null,
    onSettled,
  }
  recognition.onstart = () => {
    session.started = true
    session.lastVoiceAt = performance.now()
    sink.setRecording(true)
    playVoiceStart()
    watchSilence(session)
  }
  recognition.onresult = (event) => {
    session.lastVoiceAt = performance.now()
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i]
      if (result.isFinal) session.finals.set(i, result[0].transcript)
    }
  }
  recognition.onerror = (event) => {
    const key = nativeErrorKey(event.error)
    if (key) sink.fail(key)
  }
  // Fires on user stop, silence, platform end, error, and abort alike — the
  // single place the session settles and the transcript is emitted.
  recognition.onend = () => {
    clearTimer(session)
    const text = joinFinals(session.finals)
    sink.setRecording(false)
    sink.setProcessing(false)
    if (session.started) playVoiceStop()
    session.onSettled()
    if (!session.suppressed && text) sink.emit(text)
  }
  return session
}

/**
 * Speech-to-text on the browser's built-in `SpeechRecognition` — the mobile
 * counterpart of the streaming engine, with the same contract: `start` begins
 * listening, `stop` (or silence, or the platform ending the session) emits the
 * final transcript to `onTranscript` exactly once. The platform handles the
 * microphone, so `loading` is always false and `stream` is always null (the
 * recording island shows its resting waveform).
 */
export function useNativeDictation({
  onTranscript,
}: {
  /** Called once, with the final transcript, after the session ends. */
  onTranscript: (sessionText: string) => void
}): DictationSession {
  const { t } = useTranslation()
  const [recording, setRecording] = useState(false)
  // True from stop until `onend` delivers the last finalized segments.
  const [processing, setProcessing] = useState(false)

  // Latest onTranscript/t, so the long-lived handlers never hold stale ones.
  const onTranscriptRef = useRef(onTranscript)
  onTranscriptRef.current = onTranscript
  const tRef = useRef(t)
  tRef.current = t

  const sessionRef = useRef<Session | null>(null)

  const start = useCallback(() => {
    if (sessionRef.current) return
    // Speech APIs are disabled outside a secure context — say so instead of
    // flashing on then erroring.
    if (!window.isSecureContext) {
      toast.error(tRef.current('voice.insecure'))
      return
    }
    const session = createSession(
      {
        setRecording,
        setProcessing,
        emit: (text) => onTranscriptRef.current(text),
        fail: (key) => toast.error(tRef.current(key)),
      },
      () => {
        sessionRef.current = null
      },
    )
    if (!session) {
      toast.error(tRef.current('voice.unsupported'))
      return
    }
    setProcessing(false)
    sessionRef.current = session
    try {
      session.recognition.start()
    } catch {
      sessionRef.current = null
      toast.error(tRef.current('voice.busy'))
    }
  }, [])

  const stop = useCallback(() => {
    if (sessionRef.current) stopSession(sessionRef.current)
  }, [])

  // Unmount: abort without emitting into a gone surface.
  useEffect(
    () => () => {
      const session = sessionRef.current
      if (!session) return
      session.suppressed = true
      clearTimer(session)
      session.recognition.abort()
      sessionRef.current = null
    },
    [],
  )

  return {
    recording,
    loading: false,
    processing,
    stream: null,
    start,
    stop,
  }
}
